<template>
  <div>
    <h3 class="text-subtitle-1 font-weight-bold mb-4">
      {{ $t('onboarding.wizard.data-entry.company-info') }}
    </h3>

    <OnboardingFormField
      class="mb-4"
      field-key="street"
      icon="mdi-map-marker"
      :label="$t('onboarding.wizard.data-entry.street')"
    >
      <v-text-field
        :model-value="formData.street"
        id="onboarding-field-street"
        data-ai-field="street"
        :class="fieldClass('street')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :placeholder="$t('onboarding.wizard.data-entry.street-placeholder')"
        hide-details
        @update:model-value="updateField('street', $event)"
      />
    </OnboardingFormField>

    <v-row class="mb-4">
      <v-col
        cols="12"
        md="6"
      >
        <OnboardingFormField
          field-key="city"
          icon="mdi-city"
          :label="$t('onboarding.wizard.data-entry.city')"
        >
          <v-text-field
            :model-value="formData.city"
            id="onboarding-field-city"
            data-ai-field="city"
            :class="fieldClass('city')"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            :placeholder="$t('onboarding.wizard.data-entry.city-placeholder')"
            hide-details
            @update:model-value="updateField('city', $event)"
          />
        </OnboardingFormField>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <OnboardingFormField
          field-key="zipcode"
          icon="mdi-mailbox"
          :label="$t('onboarding.wizard.data-entry.zipcode')"
        >
          <v-text-field
            :model-value="formData.zipcode"
            id="onboarding-field-zipcode"
            data-ai-field="zipcode"
            :class="fieldClass('zipcode')"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            :placeholder="$t('onboarding.wizard.data-entry.zipcode-placeholder')"
            hide-details
            @update:model-value="updateField('zipcode', $event)"
          />
        </OnboardingFormField>
      </v-col>
    </v-row>

    <OnboardingFormField
      class="mb-4"
      field-key="country"
      icon="mdi-earth"
      :label="$t('onboarding.wizard.data-entry.country')"
    >
      <v-select
        v-model="countrySelectModel"
        id="onboarding-field-country"
        data-ai-field="country"
        :items="countrySelectItems"
        item-title="title"
        item-value="value"
        :class="fieldClass('country')"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        clearable
        hide-details
        :placeholder="$t('onboarding.wizard.data-entry.country-placeholder')"
        @focus="onFieldFocus('country')"
      />
    </OnboardingFormField>

    <template v-if="isUsOnboarding">
      <OnboardingFormField
        class="mb-4"
        field-key="state"
        icon="mdi-map"
        :label="$t('onboarding.wizard.data-entry.state')"
      >
        <v-text-field
          :model-value="formData.state"
          id="onboarding-field-state"
          data-ai-field="state"
          :class="fieldClass('state')"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          :placeholder="$t('onboarding.wizard.data-entry.state-placeholder')"
          hide-details
          @update:model-value="updateField('state', $event)"
        />
      </OnboardingFormField>

      <OnboardingFormField
        class="mb-4"
        field-key="brokerageName"
        icon="mdi-domain"
        :label="$t('onboarding.wizard.data-entry.brokerage-name')"
      >
        <v-text-field
          :model-value="formData.brokerageName"
          id="onboarding-field-brokerageName"
          data-ai-field="brokerageName"
          :class="fieldClass('brokerageName')"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          :placeholder="$t('onboarding.wizard.data-entry.brokerage-name-placeholder')"
          hide-details
          @update:model-value="updateField('brokerageName', $event)"
        />
      </OnboardingFormField>

      <OnboardingFormField
        class="mb-4"
        field-key="licenseNumber"
        icon="mdi-card-account-details"
        :label="$t('onboarding.wizard.data-entry.license-number')"
      >
        <v-text-field
          :model-value="formData.licenseNumber"
          id="onboarding-field-licenseNumber"
          data-ai-field="licenseNumber"
          :class="fieldClass('licenseNumber')"
          variant="outlined"
          density="comfortable"
          rounded="lg"
          :placeholder="$t('onboarding.wizard.data-entry.license-number-placeholder')"
          hide-details
          @update:model-value="updateField('licenseNumber', $event)"
        />
      </OnboardingFormField>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useOnboardingDataEntryContext } from '../../composables/onboardingDataEntryContext'
import { useOnboardingCountrySelect } from '../../composables/useOnboardingCountrySelect'
import OnboardingFormField from './OnboardingFormField.vue'

const { formData, updateField, fieldClass, onFieldFocus, isUsOnboarding } =
  useOnboardingDataEntryContext()
const { countrySelectItems, countrySelectModel } = useOnboardingCountrySelect({
  country: () => formData.value.country,
  updateField,
})
</script>
